import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Snackbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Block as RevokeIcon,
  DriveFileRenameOutline as RenameIcon,
  VpnKey as KeyIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  RestartAlt as RestartIcon,
  SystemUpdateAlt as UpdateIcon,
  Handshake as HandshakeIcon,
  Dns as DnsIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  MenuBook as GuideIcon,
} from '@mui/icons-material';
import { agentsAPI, getSelectedAgent, setSelectedAgent } from '../services/api';
import AgentEnrollDialog from '../components/AgentEnrollDialog';
import AgentDeployGuide from '../components/AgentDeployGuide';
import TrustsPanel from '../components/TrustsPanel';

/**
 * Agents — the control plane for the domain controllers this app manages.
 *
 * This page exists because the app no longer holds a credential with sudo on a
 * DC. It dispatches named operations to agents, and everything an operator
 * needs to reason about that lives here: which DCs are enrolled and reachable,
 * every operation that was ever dispatched and who approved it, and whether the
 * SSH fallback is switched on.
 */

const POLL_MS = 10000;

// Matches the tiers in agent/src/ops.rs. The three gated ones are called out in
// colour because "this operation needed a human" is the single most important
// thing to be able to see at a glance in the activity log.
const TIER_COLOR = {
  read: 'default',
  write: 'info',
  destructive: 'error',
  schema: 'warning',
  service: 'warning',
};

const STATUS_COLOR = {
  succeeded: 'success',
  failed: 'error',
  rejected: 'warning',
  queued: 'default',
  dispatched: 'info',
};

function relative(ts) {
  if (!ts) return 'never';
  const secs = Math.round((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return new Date(ts).toLocaleDateString();
}

/** A live dot. Pulses only when the agent is actually checking in. */
function LiveDot({ live }) {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        width: 10,
        height: 10,
        borderRadius: '50%',
        display: 'inline-block',
        flexShrink: 0,
        bgcolor: live ? 'success.main' : 'text.disabled',
        // opacity/transform only, and stopped entirely under reduced motion --
        // a pulsing dot is exactly the kind of thing that hurts.
        animation: live ? 'agentPulse 2s ease-in-out infinite' : 'none',
        '@keyframes agentPulse': {
          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
          '50%': { opacity: 0.45, transform: 'scale(0.86)' },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    />
  );
}

function AgentCard({ agent, onReissue, onRevoke, onService, onRename, busy, isAdmin }) {
  const revoked = agent.status === 'revoked';
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderColor: agent.is_live ? 'success.main' : 'divider',
        transition: 'transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <LiveDot live={agent.is_live} />
          <Typography variant="h6" sx={{ flexGrow: 1, wordBreak: 'break-word' }}>
            {agent.name}
          </Typography>
          {agent.is_default && <Chip size="small" label="default" />}
        </Stack>

        {agent.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {agent.description}
          </Typography>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
          <Chip
            size="small"
            color={revoked ? 'error' : agent.is_live ? 'success' : 'default'}
            label={
              revoked
                ? 'revoked'
                : agent.is_live
                ? 'online'
                : agent.enrollment_pending
                ? 'awaiting enrollment'
                : 'not checking in'
            }
          />
          {agent.operations > 0 && <Chip size="small" variant="outlined" label={`${agent.operations} ops`} />}
          {agent.samba_version && (
            <Chip size="small" variant="outlined" label={`Samba ${agent.samba_version}`} />
          )}
          {agent.version && (
            /* up_to_date is deliberately three-state: null means the agent has
               not reported a version yet, and calling that "out of date" would
               accuse a healthy agent of something it has not done. */
            <Tooltip
              title={
                agent.up_to_date === false
                  ? `This hub expects agent v${agent.expected_version}. Re-run the installer on this DC.`
                  : agent.up_to_date === true
                  ? 'Agent matches the version this hub expects'
                  : 'Agent version not reported yet'
              }
            >
              <Chip
                size="small"
                variant={agent.up_to_date === false ? 'filled' : 'outlined'}
                color={agent.up_to_date === false ? 'warning' : 'default'}
                label={
                  agent.up_to_date === false
                    ? `agent v${agent.version} — update available`
                    : `agent v${agent.version}`
                }
              />
            </Tooltip>
          )}
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <Stack spacing={0.5}>
          {agent.hostname && (
            <Typography variant="caption" color="text.secondary">
              host <strong>{agent.hostname}</strong>
            </Typography>
          )}
          {agent.realm && (
            <Typography variant="caption" color="text.secondary">
              realm <strong>{agent.realm}</strong>
            </Typography>
          )}
          {agent.base_dn && (
            /* The directory root this agent manages. With more than one DC the
               DN is what tells you which directory you are actually looking at,
               so it must be readable rather than truncated -- it is long, so it
               wraps instead of widening the card. */
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ wordBreak: 'break-all' }}
            >
              base DN <strong>{agent.base_dn}</strong>
            </Typography>
          )}
          {agent.ldap_uri && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ wordBreak: 'break-all' }}
            >
              ldap <strong>{agent.ldap_uri}</strong>
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            last seen <strong>{relative(agent.last_seen)}</strong>
          </Typography>
        </Stack>

        {!agent.is_live && !revoked && (
          <Alert severity={agent.enrollment_pending ? 'info' : 'warning'} sx={{ mt: 1.5 }}>
            {agent.enrollment_pending
              ? 'An enrollment token is outstanding. Run the agent on the DC to complete enrollment.'
              : 'This agent has not checked in. Operations sent to it will fail until it returns.'}
          </Alert>
        )}

        {isAdmin && (
          <>
            {/* Service control belongs HERE, not on a global Diagnostics page.
                "Restart the service" is meaningless until you say which domain
                controller, and a page-level button silently acts on whichever
                agent happens to be selected. These target THIS card's agent by
                name. */}
            {!revoked && agent.is_live && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" color="text.secondary">
                  directory service on <strong>{agent.name}</strong>
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<StartIcon />}
                    disabled={busy === agent.name}
                    onClick={() => onService(agent, 'start')}
                  >
                    Start
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<RestartIcon />}
                    disabled={busy === agent.name}
                    onClick={() => onService(agent, 'restart')}
                  >
                    Restart
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<StopIcon />}
                    disabled={busy === agent.name}
                    onClick={() => onService(agent, 'stop')}
                  >
                    Stop
                  </Button>
                </Stack>
              </>
            )}

            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
              <Button size="small" startIcon={<KeyIcon />} onClick={() => onReissue(agent)}>
                Enrollment token
              </Button>
              <Button size="small" startIcon={<RenameIcon />} onClick={() => onRename(agent)}>
                Rename
              </Button>
              {!revoked && (
                <Button size="small" color="error" startIcon={<RevokeIcon />} onClick={() => onRevoke(agent)}>
                  Revoke
                </Button>
              )}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function TransportPanel({ policy, isAdmin, onChanged, setError }) {
  const [reason, setReason] = useState('');
  const [ttl, setTtl] = useState(3600);
  const [busy, setBusy] = useState(false);

  if (!policy) return <CircularProgress />;

  const active = policy.ssh_fallback_active;

  const toggle = async (enable) => {
    setBusy(true);
    setError('');
    try {
      await agentsAPI.setPolicy(
        enable ? { enabled: true, reason: reason.trim(), ttl_seconds: ttl } : { enabled: false }
      );
      setReason('');
      onChanged();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Could not change the transport policy');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Alert severity={active ? 'warning' : 'success'} sx={{ mb: 2 }}>
        <AlertTitle>
          SSH fallback is {active ? 'ENABLED' : 'disabled'}
        </AlertTitle>
        {active ? (
          <>
            The app may reach the domain controller over SSH with a decryptable sudo password.
            Every use is logged. Turn this off as soon as the agent is healthy.
          </>
        ) : (
          <>
            The app reaches domain controllers only through agents. It holds no credential with
            sudo on a DC, and it will refuse rather than fall back on its own.
          </>
        )}
      </Alert>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Why this switch exists
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The SSH path was kept, not deleted — if every agent is down you still need a way in.
          But it is off by default and stays off until an administrator turns it on here, with a
          reason and an expiry. A fallback that engages by itself is not a fallback, it is the
          default.
        </Typography>
      </Paper>

      {active && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack spacing={0.5}>
            <Typography variant="body2">
              <strong>Enabled by:</strong> {policy.enabled_by || '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Reason:</strong> {policy.reason || '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Since:</strong>{' '}
              {policy.enabled_at ? new Date(policy.enabled_at).toLocaleString() : '—'}
            </Typography>
            <Typography variant="body2">
              <strong>Expires:</strong>{' '}
              {policy.expires_at
                ? new Date(policy.expires_at).toLocaleString()
                : 'never (open-ended grant)'}
            </Typography>
          </Stack>
        </Paper>
      )}

      {!isAdmin ? (
        <Alert severity="info">
          Only an administrator can change the transport policy.
        </Alert>
      ) : active ? (
        <Button variant="contained" color="primary" disabled={busy} onClick={() => toggle(false)}>
          {busy ? 'Working…' : 'Disable SSH fallback'}
        </Button>
      ) : (
        <Stack spacing={2}>
          <TextField
            label="Reason for enabling"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            required
            helperText="At least 10 characters. The failure mode of a break-glass switch is not that it gets used — it is that it gets used once and left on with nobody remembering why."
          />
          <TextField
            select
            label="Automatically disable after"
            value={ttl}
            onChange={(e) => setTtl(Number(e.target.value))}
            sx={{ maxWidth: 320 }}
          >
            <MenuItem value={900}>15 minutes</MenuItem>
            <MenuItem value={3600}>1 hour</MenuItem>
            <MenuItem value={14400}>4 hours</MenuItem>
            <MenuItem value={0}>Do not expire</MenuItem>
          </TextField>
          <Box>
            <Button
              variant="contained"
              color="warning"
              disabled={busy || reason.trim().length < 10}
              onClick={() => toggle(true)}
            >
              {busy ? 'Working…' : 'Enable SSH fallback'}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
}

function ActivityTable({ jobs, agents, filters, setFilters, compact }) {
  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <TextField
          select
          size="small"
          label="Agent"
          value={filters.agent}
          onChange={(e) => setFilters({ ...filters, agent: e.target.value })}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All agents</MenuItem>
          {agents.map((a) => (
            <MenuItem key={a.name} value={a.name}>
              {a.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Outcome"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All outcomes</MenuItem>
          <MenuItem value="succeeded">Succeeded</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
          <MenuItem value="rejected">Refused by agent</MenuItem>
          <MenuItem value="queued">Queued</MenuItem>
          <MenuItem value="dispatched">Dispatched</MenuItem>
        </TextField>
      </Stack>

      {jobs.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No operations recorded yet. Every operation the app dispatches to a domain controller
            appears here, with who asked for it and who approved it.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Operation</TableCell>
                <TableCell>Tier</TableCell>
                {!compact && <TableCell>Agent</TableCell>}
                <TableCell>Outcome</TableCell>
                {!compact && <TableCell>Requested by</TableCell>}
                <TableCell>Approved by</TableCell>
                {!compact && <TableCell>Detail</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((j) => (
                <TableRow key={j.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={new Date(j.created_at).toLocaleString()}>
                      <span>{relative(j.created_at)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{j.op}</TableCell>
                  <TableCell>
                    <Chip size="small" label={j.tier} color={TIER_COLOR[j.tier] || 'default'} />
                  </TableCell>
                  {!compact && <TableCell>{j.agent}</TableCell>}
                  <TableCell>
                    <Chip
                      size="small"
                      variant={j.status === 'succeeded' ? 'filled' : 'outlined'}
                      label={j.status === 'rejected' ? 'refused' : j.status}
                      color={STATUS_COLOR[j.status] || 'default'}
                    />
                  </TableCell>
                  {!compact && <TableCell>{j.requested_by || '—'}</TableCell>}
                  <TableCell>
                    {j.approved_by ? (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        <Tooltip
                          title={
                            (j.approver_role ? `role: ${j.approver_role}` : '') +
                            (j.approved_at ? ` · ${new Date(j.approved_at).toLocaleString()}` : '')
                          }
                        >
                          <Chip size="small" color="secondary" label={j.approved_by} />
                        </Tooltip>
                        {/* Self-approval is allowed for admins. Showing it is the
                            point: the control is attributability, not a second
                            pair of eyes, so it must never be silent. */}
                        {j.self_approved && (
                          <Tooltip title="The administrator who requested this also authorised it">
                            <Chip size="small" variant="outlined" color="warning" label="self" />
                          </Tooltip>
                        )}
                      </Stack>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  {!compact && (
                    <TableCell
                      sx={{
                        maxWidth: 320,
                        color: j.error ? 'error.main' : 'text.secondary',
                        fontSize: '0.78rem',
                      }}
                    >
                      {j.error || Object.keys(j.args || {}).join(', ') || '—'}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

function Agents() {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser);
  const compact = useMediaQuery('(max-width:900px)');

  const [tab, setTab] = useState(0);
  const [agents, setAgents] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ agent: '', status: '' });

  const [enrollOpen, setEnrollOpen] = useState(false);
  const [reissueTarget, setReissueTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameForm, setRenameForm] = useState({ name: '', description: '' });
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState('');
  const [renameNote, setRenameNote] = useState('');
  const [guideOpen, setGuideOpen] = useState(false);
  // Which agent currently has a service action in flight, and the confirmation
  // dialog for the two that cause an outage.
  const [serviceBusy, setServiceBusy] = useState('');
  const [serviceConfirm, setServiceConfirm] = useState(null);
  const [serviceResult, setServiceResult] = useState('');
  const [release, setRelease] = useState(null);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [manifestText, setManifestText] = useState('');
  const [signatureText, setSignatureText] = useState('');
  const [releaseBusy, setReleaseBusy] = useState(false);

  // The address agents must dial, as the BACKEND reports it.
  //
  // Deliberately not window.location.origin: the UI is reached over the public
  // edge, while agents dial a separate certificate-pinned TLS listener on the
  // LAN. Printing the browser's origin gave operators install instructions that
  // could not work. Empty means the deployment has not set AGENT_HUB_URL, and
  // the enrollment dialog says so rather than inventing an address.
  const [hubUrl, setHubUrl] = useState('');

  // Service control for ONE named domain controller.
  //
  // `stop` and `restart` are confirmed first. On a single-DC domain, stopping
  // the directory service is a domain-wide outage -- authentication, DNS and
  // file access all go with it -- and a button that does that on one click is
  // a button that will eventually be clicked by accident.
  const submitRename = async () => {
    if (!renameTarget) return;
    setRenameBusy(true);
    setRenameError('');
    try {
      const res = await agentsAPI.rename(renameTarget.name, {
        name: renameForm.name.trim(),
        description: renameForm.description,
      });
      // If the agent currently selected in the toolbar was the one renamed,
      // move the selection with it -- otherwise every page would keep sending
      // a name that no longer exists.
      if (getSelectedAgent() === renameTarget.name) {
        setSelectedAgent(renameForm.name.trim());
        // Everything on screen was read from the OLD name; drop it.
        window.dispatchEvent(new CustomEvent('samba-agent-changed',
          { detail: { name: renameForm.name.trim() } }));
      }
      setRenameTarget(null);
      await load();
      setRenameNote(res.data?.note || '');
    } catch (e) {
      setRenameError(e.response?.data?.detail || 'Rename failed.');
    } finally {
      setRenameBusy(false);
    }
  };

  const handleService = (agent, action) => {
    if (action === 'start') {
      runService(agent.name, action);
    } else {
      setServiceConfirm({ name: agent.name, action });
    }
  };

  const runService = async (name, action) => {
    setServiceBusy(name);
    setServiceResult('');
    setError('');
    try {
      const fn = {
        start: agentsAPI.startService,
        stop: agentsAPI.stopService,
        restart: agentsAPI.restartService,
      }[action];
      await fn(name);
      setServiceResult(`${action} sent to ${name}`);
      // Re-read rather than assuming it worked: the agent reports the
      // resulting state, and the card should show what actually happened.
      await load({ quiet: true });
    } catch (e) {
      setError(
        e.response?.data?.error ||
          e.response?.data?.detail ||
          `Could not ${action} the directory service on ${name}`
      );
    } finally {
      setServiceBusy('');
      setServiceConfirm(null);
    }
  };

  // Staging a release and ANNOUNCING it are two deliberate acts. Uploading
  // never starts a rollout, because the point of staging is to check it first
  // -- with `samba-agent verify-update`, which answers "will my agents accept
  // this?" from the same code path they use.
  const stageRelease = async () => {
    setReleaseBusy(true);
    setError('');
    try {
      const r = await agentsAPI.stageRelease(manifestText.trim(), signatureText.trim());
      setRelease(r.data);
      setServiceResult(`release ${r.data.version} staged — not yet announced`);
      setManifestText('');
      setSignatureText('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not stage the release');
    } finally {
      setReleaseBusy(false);
    }
  };

  const setRollout = async (on) => {
    setReleaseBusy(true);
    setError('');
    try {
      const r = await agentsAPI.setRollout(on);
      setRelease((prev) => ({ ...prev, ...r.data }));
      setServiceResult(on
        ? `rolling out ${r.data.version}; agents update on their next check-in`
        : 'rollout stopped');
      await load({ quiet: true });
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not change the rollout');
    } finally {
      setReleaseBusy(false);
    }
  };

  const load = useCallback(
    async (opts = {}) => {
      try {
        const [a, j, p] = await Promise.all([
          agentsAPI.list(),
          agentsAPI.jobs({
            limit: 100,
            ...(filters.agent ? { agent: filters.agent } : {}),
            ...(filters.status ? { status: filters.status } : {}),
          }),
          agentsAPI.getPolicy(),
        ]);
        try {
          const rel = await agentsAPI.getRelease();
          setRelease(rel.data);
        } catch {
          // A deployment that has never staged a release is normal, not an error.
          setRelease({ staged: false });
        }
        setAgents(a.data.agents || []);
        setHubUrl(a.data.hub_url || '');
        setJobs(j.data.jobs || []);
        setPolicy(p.data);
        if (!opts.quiet) setError('');
      } catch (e) {
        // A background poll must not clear the screen or spam the banner.
        if (!opts.quiet) {
          setError(e.response?.data?.detail || e.message || 'Could not load agents');
        }
      } finally {
        setLoading(false);
      }
    },
    [filters.agent, filters.status]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Liveness is only meaningful if it is current: a page that says "online"
  // from thirty minutes ago is worse than one that says nothing. Polling stops
  // while the tab is hidden.
  useEffect(() => {
    const tick = () => {
      if (!document.hidden) load({ quiet: true });
    };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  const doRevoke = async () => {
    try {
      await agentsAPI.revoke(revokeTarget.name);
      setRevokeTarget(null);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Revoke failed');
      setRevokeTarget(null);
    }
  };

  const liveCount = agents.filter((a) => a.is_live).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={2}
        sx={{ mb: 1 }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" gutterBottom>
            Agents
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {agents.length === 0
              ? 'No domain controllers enrolled yet.'
              : `${liveCount} of ${agents.length} agent${agents.length === 1 ? '' : 's'} online.`}{' '}
            Operations are dispatched by name to an agent, which executes only its own allowlist —
            never a shell.
          </Typography>
        </Box>
        {/* Four actions do not fit one phone-width row: the last one was
            clipped off the right edge, which on a touch screen means the
            primary action is simply unreachable. Wrapping keeps every button
            on screen at 390px and changes nothing at desktop width. */}
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{ flexWrap: 'wrap', maxWidth: '100%', rowGap: 1 }}
        >
          <Button startIcon={<GuideIcon />} onClick={() => setGuideOpen(true)}>
            Deploy a DC
          </Button>
          {isAdmin && (
            <Button
              startIcon={<UpdateIcon />}
              color={release?.rollout ? 'warning' : 'inherit'}
              onClick={() => setUpdateOpen(true)}
            >
              {release?.rollout ? `Rolling out ${release.version}` : 'Update agents'}
            </Button>
          )}
          <Button startIcon={<RefreshIcon />} onClick={() => load()}>
            Refresh
          </Button>
          {isAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEnrollOpen(true)}>
              Enroll DC
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ my: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {policy?.ssh_fallback_active && (
        <Alert severity="warning" sx={{ my: 2 }}>
          <AlertTitle>SSH fallback is currently enabled</AlertTitle>
          Enabled by <strong>{policy.enabled_by || 'unknown'}</strong>
          {policy.reason ? ` — ${policy.reason}` : ''}. Disable it on the Transport tab once the
          agent is healthy.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        // minWidth:0 matters more than `variant="scrollable"` here. A scrollable
        // MuiTabs still reports the full width of its labels as its min-content,
        // and `main` in the app Layout is a flex item with min-width:auto -- so
        // the tab strip silently widened the whole PAGE at phone width instead
        // of scrolling inside itself. This was the only horizontal overflow on
        // the page; the cards and the table were innocent.
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, minWidth: 0, maxWidth: '100%' }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab icon={<DnsIcon />} iconPosition="start" label="Domain controllers" />
        <Tab icon={<HistoryIcon />} iconPosition="start" label="Activity" />
        <Tab icon={<HandshakeIcon />} iconPosition="start" label="Trusts" />
        <Tab icon={<SecurityIcon />} iconPosition="start" label="Transport" />
      </Tabs>

      {tab === 0 &&
        (agents.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <DnsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" gutterBottom>
              No domain controllers enrolled
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 560, mx: 'auto' }}>
              Enroll a DC to manage it without giving this app a credential with sudo on it. You
              can enroll several — each one is managed from here.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Button startIcon={<GuideIcon />} onClick={() => setGuideOpen(true)}>
                How to deploy a DC
              </Button>
              {isAdmin && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEnrollOpen(true)}>
                  Enroll a domain controller
                </Button>
              )}
            </Stack>
          </Paper>
        ) : (
          /* CSS grid rather than MUI <Grid container spacing>, whose negative
             margin and width:calc(100% + n) overflowed the body horizontally at
             phone width. A plain grid with `gap` cannot. */
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              // minmax(0, 1fr) at EVERY breakpoint, including xs. A bare `1fr`
              // is minmax(auto, 1fr), and `auto` refuses to shrink below the
              // content's min-content width -- so one long unbreakable value
              // (a DN, an IP, a hostname) silently widens the whole page.
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              },
            }}
          >
            {agents.map((a) => (
              <AgentCard
                key={a.name}
                agent={a}
                isAdmin={isAdmin}
                onReissue={(ag) => setReissueTarget(ag)}
                onRevoke={(ag) => setRevokeTarget(ag)}
                onService={handleService}
                onRename={(ag) => { setRenameTarget(ag); setRenameForm({ name: ag.name, description: ag.description || '' }); }}
                busy={serviceBusy}
              />
            ))}
          </Box>
        ))}

      {tab === 1 && (
        <ActivityTable
          jobs={jobs}
          agents={agents}
          filters={filters}
          setFilters={setFilters}
          compact={compact}
        />
      )}

      {tab === 2 && (
        <TrustsPanel
          /* Scoped to the SELECTED controller: a trust belongs to a domain,
             and with several enrolled the panel must say whose it is showing. */
          agentName={getSelectedAgent() || agents.find((a) => a.is_default)?.name || agents[0]?.name}
          isAdmin={isAdmin}
          onError={setError}
        />
      )}

      {tab === 3 && (
        <TransportPanel
          policy={policy}
          isAdmin={isAdmin}
          onChanged={() => load()}
          setError={setError}
        />
      )}

      <AgentDeployGuide open={guideOpen} onClose={() => setGuideOpen(false)} hubUrl={hubUrl} />

      <AgentEnrollDialog
        open={enrollOpen || Boolean(reissueTarget)}
        existingAgent={reissueTarget}
        hubUrl={hubUrl}
        onClose={() => {
          setEnrollOpen(false);
          setReissueTarget(null);
        }}
        onEnrolled={() => load()}
      />

      {/* Renaming an agent. Safe for the control channel -- a client
          certificate is matched to its agent by serial, not by its CN -- but it
          DOES change the value callers must send as X-Samba-Agent, so the
          dialog says so before the change rather than after. */}
      {renameNote && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRenameNote('')}>
          {renameNote}
        </Alert>
      )}
      <Dialog open={Boolean(renameTarget)} onClose={() => setRenameTarget(null)}
              maxWidth="sm" fullWidth>
        <DialogTitle>Rename “{renameTarget?.name}”</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            The agent keeps working: its certificate is matched by serial, not by
            name, so mTLS is unaffected. What changes is the value anything
            outside this application must send as{' '}
            <code>X-Samba-Agent</code>. API keys bound to this agent are
            unaffected — they are bound by id.
          </Alert>
          <TextField
            autoFocus fullWidth margin="dense" label="Name"
            value={renameForm.name}
            onChange={(e) => setRenameForm({ ...renameForm, name: e.target.value })}
            helperText="Lowercase letters, digits and hyphens; must start and end alphanumeric."
          />
          <TextField
            fullWidth margin="dense" label="Description"
            value={renameForm.description}
            onChange={(e) => setRenameForm({ ...renameForm, description: e.target.value })}
            helperText="Free text, shown on the card. For example: which site or realm this DC serves."
          />
          {renameError && <Alert severity="error" sx={{ mt: 1 }}>{renameError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitRename}
                  disabled={renameBusy || !renameForm.name.trim()}>
            {renameBusy ? 'Renaming…' : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(revokeTarget)} onClose={() => setRevokeTarget(null)}>
        <DialogTitle>Revoke “{revokeTarget?.name}”?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            The agent's token stops working immediately and anything it had queued is failed. The
            agent stands itself down when it next checks in, and will not restart on its own.
            <Alert severity="info" sx={{ mt: 2 }}>
              Re-enrolling needs a new enrollment token and a visit to the domain controller. Use
              this when the host is compromised or decommissioned — not to pause it.
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={doRevoke}>
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stopping or restarting the directory service is confirmed, and the
          confirmation says what it actually costs. On a single-DC domain a
          stop takes authentication, DNS and file access down with it. */}
      <Dialog open={Boolean(serviceConfirm)} onClose={() => setServiceConfirm(null)}>
        <DialogTitle>
          {serviceConfirm?.action === 'stop' ? 'Stop' : 'Restart'} the directory service on “
          {serviceConfirm?.name}”?
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            This acts on <strong>{serviceConfirm?.name}</strong> only — no other domain controller
            is touched.
            <Alert severity="warning" sx={{ mt: 2 }}>
              {serviceConfirm?.action === 'stop'
                ? 'While the service is stopped this controller authenticates nobody and answers no DNS. If it is the only controller in the domain, that is a domain-wide outage, and starting it again is a separate deliberate action.'
                : 'Sessions served by this controller are interrupted while it restarts. If it is the only controller in the domain, authentication fails until it is back.'}
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setServiceConfirm(null)}>Cancel</Button>
          <Button
            color={serviceConfirm?.action === 'stop' ? 'error' : 'warning'}
            variant="contained"
            disabled={Boolean(serviceBusy)}
            onClick={() => runService(serviceConfirm.name, serviceConfirm.action)}
          >
            {serviceConfirm?.action === 'stop' ? 'Stop it' : 'Restart it'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pushing an agent update.
          The hub ANNOUNCES; it never vouches. It holds no signing key and
          cannot produce one, so a hub that has been taken over can point the
          fleet at anything it likes and every agent will refuse it: each one
          verifies an ed25519 signature against a key compiled into its own
          binary, and refuses any version that is not strictly newer. */}
      <Dialog open={updateOpen} onClose={() => setUpdateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Update agents</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This hub only <strong>announces</strong> a release. It holds no signing key. Every
            agent verifies the signature against a key compiled into its own binary and refuses
            anything that does not match — so a compromised hub cannot ship code to a domain
            controller.
          </Alert>

          {release?.staged ? (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle2">Staged release</Typography>
                <Chip size="small" label={`v${release.version}`} />
                <Chip
                  size="small"
                  color={release.rollout ? 'warning' : 'default'}
                  label={release.rollout ? 'rolling out' : 'not announced'}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                staged by <strong>{release.uploaded_by || '—'}</strong>
              </Typography>
              <Alert severity="warning" sx={{ mt: 2 }}>
                Check it before announcing. On any one DC:
                <Box component="pre" sx={{ m: 0, mt: 1, fontSize: 12, whiteSpace: 'pre-wrap' }}>
{`samba-agent verify-update \\
  --manifest update-manifest.json \\
  --signature update-manifest.sig`}
                </Box>
                That answers the only question that matters — will my agents accept this? — from
                the same code path they use.
              </Alert>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                {release.rollout ? (
                  <Button color="inherit" variant="outlined" disabled={releaseBusy}
                          onClick={() => setRollout(false)}>
                    Stop the rollout
                  </Button>
                ) : (
                  <Button color="warning" variant="contained" disabled={releaseBusy}
                          onClick={() => setRollout(true)}>
                    Announce v{release.version} to all agents
                  </Button>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                Agents update on their next check-in, one at a time as they call in. An agent
                already on this version is skipped, and read-only container agents are never
                told — their update path is a new image.
              </Typography>
            </Paper>
          ) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No release is staged.
            </Alert>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Stage a release
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Produced by <code>scripts/sign_update.py sign</code> on a machine that holds the
            release key. Staging never starts a rollout.
          </Typography>
          <TextField
            label="update-manifest.json"
            value={manifestText}
            onChange={(e) => setManifestText(e.target.value)}
            fullWidth multiline minRows={3} sx={{ mb: 2 }}
            placeholder={'{"sha256":"…","url":"https://…","version":"0.2.0"}'}
          />
          <TextField
            label="update-manifest.sig (base64)"
            value={signatureText}
            onChange={(e) => setSignatureText(e.target.value)}
            fullWidth multiline minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateOpen(false)}>Close</Button>
          <Button
            variant="contained"
            disabled={releaseBusy || !manifestText.trim() || !signatureText.trim()}
            onClick={stageRelease}
          >
            Stage it
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(serviceResult)}
        autoHideDuration={6000}
        onClose={() => setServiceResult('')}
        message={serviceResult}
      />
    </Box>
  );
}

export default Agents;
